import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { DataTable, PageHeader, SearchBox } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["invoice", "payment", "expense", "customer", "agent", "booking", "quotation",
  "settings", "user", "period", "file", "service", "vendor"];

export default function AuditLog() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audit-logs", { params: { q, record_type: type, page, limit: 50 } });
      setData(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [q, type, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="audit-page">
      <PageHeader title="Audit Log" subtitle="Every financial change — who, what, when, before & after"
        testid="audit-header"
        actions={
          <>
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search user, action, record…" testid="audit-search" />
            <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-40 bg-white border-[#E5E0D8]" data-testid="audit-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Records</SelectItem>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        } />
      <DataTable testid="audit-table" loading={loading}
        columns={[
          { key: "at", label: "When", render: (r) => <span className="text-xs num">{dayjs(r.at).format("DD MMM YYYY HH:mm")}</span> },
          { key: "user_name", label: "User", render: (r) => (
            <div><span className="font-medium">{r.user_name}</span>
              <span className="text-xs text-[#5C6B69] ml-1">({r.role})</span></div>) },
          { key: "action", label: "Action", render: (r) => (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#0D4F4A]/10 text-[#0D4F4A]">{r.action}</span>) },
          { key: "record_type", label: "Record", render: (r) => `${r.record_type}${r.record_label ? ` · ${r.record_label}` : ""}` },
          { key: "changes", label: "Change", render: (r) => (
            <span className="text-xs text-[#5C6B69] font-mono line-clamp-2 max-w-md block">
              {r.prev || r.new
                ? `${r.prev ? JSON.stringify(r.prev).slice(0, 90) : "—"} → ${r.new ? JSON.stringify(r.new).slice(0, 90) : "—"}`
                : "—"}
            </span>) },
        ]}
        rows={data.items} page={data.page} pages={data.pages} total={data.total} onPage={setPage}
        empty="No audit events yet" />
    </div>
  );
}
